import React, { useState } from "react";
import { Mail, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import Seo from "@/components/Seo";
import Header from "./Header";
import Footer from "./Footer";

type FormState = {
  name: string;
  email: string;
  company: string;
  topic: string;
  message: string;
  consent: boolean;
  website: string;
};

export default function Contact() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState<FormState>({
    name: "",
    email: "",
    company: "",
    topic: "linko",
    message: "",
    consent: false,
    website: "",
  });

  const onChange =
    (key: keyof FormState) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      const value =
        e.target.type === "checkbox"
          ? (e.target as HTMLInputElement).checked
          : e.target.value;
      setForm((f) => ({ ...f, [key]: value }) as FormState);
    };

  const validate = () => {
    if (!form.name.trim()) return t("contact.validation.nameRequired");
    if (!/^\S+@\S+\.\S+$/.test(form.email)) return t("contact.validation.emailInvalid");
    if (!form.message.trim()) return t("contact.validation.messageRequired");
    if (!form.consent) return t("contact.validation.consentRequired");
    if (form.website && form.website.trim().length > 0) return t("contact.validation.sendFailed");
    return null;
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const error = validate();
    if (error) {
      toast.error(error);
      return;
    }

    try {
      setLoading(true);
      await new Promise((r) => setTimeout(r, 800));
      toast.success(t("contact.toastSuccess"));
      setForm({ name: "", email: "", company: "", topic: "linko", message: "", consent: false, website: "" });
    } catch {
      toast.error(t("contact.toastError"));
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    "mt-3 w-full bg-transparent border-0 border-b border-border px-0 py-2.5 text-base text-foreground placeholder:text-muted-foreground/60 outline-none transition-colors duration-200 focus:border-foreground";

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      <Seo title={t("seo.contact.title")} description={t("seo.contact.desc")} path="/contact" />
      <Header />
      <main id="main" tabIndex={-1} className="flex-1 outline-none">
        {/* Hero - editorial oversized type */}
        <section className="w-full px-6 pt-16 md:pt-24 pb-12 md:pb-16">
          <div className="max-w-[1400px] mx-auto">
            <div className="flex items-center justify-between ed-eyebrow text-muted-foreground animate-fade-in">
              <span className="inline-flex items-center gap-2">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-brand ed-blink" />
                {t("contact.eyebrow")}
              </span>
              <span className="hidden md:inline">( {t("common.builtInBrazil")} )</span>
            </div>

            <div className="mt-10 md:mt-14 grid grid-cols-1 lg:grid-cols-[1fr_auto] lg:items-end gap-8">
              <h1 className="ed-mega animate-fade-in">
                <span className="block">{t("contact.title1")}</span>
                <span className="block">
                  {t("contact.title2")}
                  <span className="text-brand">.</span>
                </span>
              </h1>
              <p className="ed-serif text-2xl md:text-3xl max-w-md text-foreground/85 animate-fade-in-delay-1 lg:pb-4">
                {t("contact.sub")}
              </p>
            </div>
          </div>
        </section>

        <div className="ed-rule" />

        {/* Form + Aside */}
        <section className="w-full px-6 py-16 md:py-24">
          <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-[1.6fr_1fr] gap-16 lg:gap-24">
            <div>
              <span className="ed-eyebrow text-muted-foreground">{t("contact.formLabel")}</span>
              <form onSubmit={onSubmit} noValidate className="mt-10 space-y-10">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-10">
                  <div>
                    <label htmlFor="name" className="ed-eyebrow text-muted-foreground">
                      {t("contact.nameLabel")} <span className="text-brand">*</span>
                    </label>
                    <input id="name" type="text" required value={form.name} onChange={onChange("name")} className={inputClass} placeholder={t("contact.namePlaceholder")} />
                  </div>
                  <div>
                    <label htmlFor="email" className="ed-eyebrow text-muted-foreground">
                      {t("contact.emailLabel")} <span className="text-brand">*</span>
                    </label>
                    <input id="email" type="email" required value={form.email} onChange={onChange("email")} className={inputClass} placeholder={t("contact.emailPlaceholder")} />
                  </div>
                  <div>
                    <label htmlFor="company" className="ed-eyebrow text-muted-foreground">{t("contact.companyLabel")}</label>
                    <input id="company" type="text" value={form.company} onChange={onChange("company")} className={inputClass} placeholder={t("contact.companyPlaceholder")} />
                  </div>
                  <div>
                    <label htmlFor="topic" className="ed-eyebrow text-muted-foreground">{t("contact.topicLabel")}</label>
                    <select id="topic" value={form.topic} onChange={onChange("topic")} className={inputClass}>
                      <option value="linko">{t("contact.topicLinko")}</option>
                      <option value="partnership">{t("contact.topicPartnership")}</option>
                      <option value="support">{t("contact.topicSupport")}</option>
                      <option value="other">{t("contact.topicOther")}</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="ed-eyebrow text-muted-foreground">
                    {t("contact.messageLabel")} <span className="text-brand">*</span>
                  </label>
                  <textarea
                    id="message"
                    required
                    rows={6}
                    value={form.message}
                    onChange={onChange("message")}
                    className={inputClass}
                    placeholder={t("contact.messagePlaceholder")}
                  />
                </div>

                {/* Honeypot */}
                <div aria-hidden className="hidden">
                  <label htmlFor="website">Website</label>
                  <input id="website" type="text" value={form.website} onChange={onChange("website")} tabIndex={-1} autoComplete="off" />
                </div>

                <div className="flex items-start gap-3">
                  <input
                    id="consent"
                    type="checkbox"
                    checked={form.consent}
                    onChange={onChange("consent")}
                    className="mt-1 h-4 w-4 border border-border accent-[hsl(var(--brand))]"
                  />
                  <label htmlFor="consent" className="text-sm text-muted-foreground leading-relaxed">
                    {t("contact.consentPre")}{" "}
                    <a href="/privacy" className="text-foreground ed-link">
                      {t("contact.privacyPolicy")}
                    </a>.
                  </label>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 pt-2">
                  <button
                    type="submit"
                    disabled={loading}
                    className="ed-btn ed-btn-solid justify-center disabled:opacity-50"
                  >
                    {loading ? t("contact.sending") : t("contact.send")}
                  </button>
                  <a
                    href="mailto:contact@avoria.com"
                    className="ed-btn justify-center"
                  >
                    {t("contact.sendEmail")}
                  </a>
                </div>

                <p className="ed-serif text-lg text-muted-foreground">
                  {t("contact.responseNote")}
                </p>
              </form>
            </div>

            {/* Aside */}
            <aside className="lg:border-l lg:border-border lg:pl-16">
              <span className="ed-eyebrow text-muted-foreground">{t("contact.otherLabel")}</span>
              <h2 className="mt-6 ed-headline">
                {t("contact.otherTitle1")}<br />{t("contact.otherTitle2")}<span className="text-brand">?</span>
              </h2>

              <ul className="mt-10 divide-y divide-border border-t border-b border-border">
                <li>
                  <a
                    href="mailto:contact@avoria.com"
                    className="group flex items-center gap-4 py-5 transition-colors duration-200 hover:text-brand"
                  >
                    <Mail className="h-5 w-5 shrink-0 text-muted-foreground group-hover:text-brand transition-colors duration-200" />
                    <span className="flex flex-col">
                      <span className="ed-eyebrow text-muted-foreground">{t("contact.emailChannel")}</span>
                      <span className="mt-1 text-base">contact@avoria.com</span>
                    </span>
                  </a>
                </li>
                <li>
                  <a
                    href="/faq"
                    className="group flex items-center gap-4 py-5 transition-colors duration-200 hover:text-brand"
                  >
                    <MessageSquare className="h-5 w-5 shrink-0 text-muted-foreground group-hover:text-brand transition-colors duration-200" />
                    <span className="flex flex-col">
                      <span className="ed-eyebrow text-muted-foreground">{t("contact.questionsChannel")}</span>
                      <span className="mt-1 text-base">{t("contact.faq")}</span>
                    </span>
                  </a>
                </li>
              </ul>

              <p className="mt-10 ed-serif text-xl text-foreground/80 leading-snug">
                {t("contact.asideNote")}
              </p>
            </aside>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
